// Clears `parsley`-prefixed browser storage on sign-out so no user PII survives on a shared device.
// Default-clear: everything under the prefix goes except KEEP_KEYS (inert device config, listed below).
// Every step fails SOFT — a blocked IndexedDB or private-mode storage must never break sign-out.

import { unsubscribePushThisDevice } from '@/services/push/teardown'

const APP_KEY_PREFIX = 'parsley'

// Parsley-owned keys that are inert device config or shared bootstrap data — kept across sign-out.
const KEEP_KEYS = new Set<string>([
  'parsley-dark-mode',
  'parsley-analytics-opt-out',
  'parsley.design.mealView',
  'parsley:ingredients_cache',
  'parsley:ingredients_version'
])

const MEAL_CACHE_DB_NAME = 'parsley-meal-cache'
const PENDING_NAV_CACHE = 'parsley-pending-nav-v1'

// Remove every Parsley-owned key from a Storage area except the inert keep-list. Keys are
// collected before removal — removing while iterating by index would skip entries.
function purgeAppKeys(store: Storage): void {
  const doomed: string[] = []
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (key?.startsWith(APP_KEY_PREFIX) && !KEEP_KEYS.has(key)) doomed.push(key)
  }
  for (const key of doomed) store.removeItem(key)
}

export async function purgeClientDataOnSignOut(): Promise<void> {
  try {
    purgeAppKeys(localStorage)
  } catch {
    // storage unavailable (private mode) — nothing to purge
  }

  try {
    purgeAppKeys(sessionStorage)
  } catch {
    // storage unavailable — nothing to purge
  }

  try {
    if (typeof indexedDB !== 'undefined') indexedDB.deleteDatabase(MEAL_CACHE_DB_NAME)
  } catch {
    // IndexedDB blocked / unavailable — best-effort only
  }

  try {
    if (typeof caches !== 'undefined') await caches.delete(PENDING_NAV_CACHE)
  } catch {
    // Cache Storage unavailable — best-effort only
  }

  // Stop this device receiving the signed-out user's push notifications (fails soft itself).
  await unsubscribePushThisDevice()
}
